/**
 * Maps granular provisioning stages to UI steps for ProvisioningView.
 *
 * The backend reports 10 fine-grained stages. This function collapses them
 * into 5 user-facing steps so the progress indicator stays simple.
 */

import type { ProvisioningStep } from '@cortex/ui';

const STEP_DEFINITIONS = [
  {
    id: 'machine',
    label: 'Creating machine',
    stages: [
      'queued',
      'container_creating',
      'container_deploying',
      'container_started',
    ],
  },
  { id: 'repo', label: 'Cloning repository', stages: ['repo_cloning'] },
  {
    id: 'deps',
    label: 'Installing dependencies',
    stages: ['deps_installing'],
  },
  {
    id: 'devserver',
    label: 'Starting dev server',
    stages: ['devserver_starting'],
  },
  {
    id: 'workspace',
    label: 'Preparing workspace',
    stages: ['terminal_ready', 'workplan_loading'],
  },
] as const;

export function mapProvisioningStageToSteps(
  stage: string | null,
  error: string | null = null
): ProvisioningStep[] {
  if (!stage) {
    return STEP_DEFINITIONS.map((def, i) => ({
      id: def.id,
      label: def.label,
      status: i === 0 ? ('active' as const) : ('pending' as const),
    }));
  }

  if (stage === 'ready') {
    return STEP_DEFINITIONS.map((def) => ({
      id: def.id,
      label: def.label,
      status: 'completed' as const,
    }));
  }

  let activeStepIndex = STEP_DEFINITIONS.findIndex((def) =>
    (def.stages as readonly string[]).includes(stage)
  );
  if (activeStepIndex === -1) activeStepIndex = 0;

  return STEP_DEFINITIONS.map((def, i) => ({
    id: def.id,
    label: def.label,
    status:
      i < activeStepIndex
        ? ('completed' as const)
        : i === activeStepIndex
          ? ('active' as const)
          : ('pending' as const),
    ...(i === activeStepIndex && error ? { message: error } : {}),
  }));
}
